import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NavBarComponent.scss';

const SECTION_IDS = {
  home: ['home'],
  about: ['about'],
  trial: ['trial'], // IMPORTANT for Start Trial scroll
  features: ['features', 'our-features'],
  choose: ['choose', 'why', 'why-choose', 'choosing'],
  testimonials: ['testimonials', 'reviews'],
  contact: ['contact'],
};

const navLinks = [
  { label: 'Home', key: 'home' },
  { label: 'About', key: 'about' },
  { label: 'Start Trial', key: 'trial' }, // added here
  { label: 'Why To Choose', key: 'choose' },
  { label: 'Our Features', key: 'features' },
  { label: 'Testimonials', key: 'testimonials' },
  { label: 'Contact Us', key: 'contact' },
];

const NavBarComponent = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  /* =========================
     SCROLL DETECTION
  ========================= */
  useEffect(() => {
    const onScroll = () => setScrolled((window.scrollY || 0) > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* =========================
     CLOSE MOBILE MENU ON RESIZE
  ========================= */
  useEffect(() => {
    if (!isOpen) return;

    const closeOnResize = () => {
      if (window.innerWidth > 1024) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', closeOnResize);
    return () => window.removeEventListener('resize', closeOnResize);
  }, [isOpen]);

  /* =========================
     NAV OFFSET (IMPORTANT)
  ========================= */
  const getNavOffset = () => {
    const nav = document.querySelector('.nav');
    return nav ? nav.offsetHeight + 8 : 80;
  };

  /* =========================
     SCROLL FUNCTION (FIXED)
  ========================= */
  const scrollToSection = useCallback((key) => {
    if (key === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const candidates = SECTION_IDS[key] || [key];

    for (let id of candidates) {
      const el = document.getElementById(id);
      if (el) {
        const y =
          el.getBoundingClientRect().top +
          window.scrollY -
          getNavOffset();

        window.scrollTo({
          top: Math.max(0, y),
          behavior: 'smooth',
        });

        return;
      }
    }

    console.warn(`Section not found: ${key}`);
  }, []);

  /* =========================
     HANDLE ROUTE CHANGE SCROLL
  ========================= */
  useEffect(() => {
    const state = location.state;

    if (location.pathname === '/' && state?.__scrollTo) {
      requestAnimationFrame(() => {
        scrollToSection(state.__scrollTo);
        navigate('.', { replace: true, state: {} });
      });
    }
  }, [location, navigate, scrollToSection]);

  /* =========================
     NAVIGATION HANDLER
  ========================= */
  const go = useCallback(
    (key) => {
      setIsOpen(false);

      if (location.pathname === '/') {
        scrollToSection(key);
      } else {
        navigate('/', {
          state: { __scrollTo: key, __ts: Date.now() },
        });
      }
    },
    [location.pathname, navigate, scrollToSection]
  );

  /* =========================
     RENDER
  ========================= */
  return (
    <header className={`nav ${scrolled ? 'is-scrolled' : 'is-top'}`}>
      <div className="nav__inner">
        {/* LOGO */}
        <a
          href="#home"
          className="nav__brand"
          onClick={(e) => {
            e.preventDefault();
            go('home');
          }}
        >
          <span className="brand__mark">Plan My Guest</span>
        </a>

        {/* DESKTOP NAV */}
        <nav className="nav__links">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={`#${link.key}`}
              className={`nav__link ${
                link.key === 'trial' ? 'nav__link--highlight' : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                go(link.key);
              }}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* BURGER */}
        <div className="nav__actions">
          <button
            className={`burger ${isOpen ? 'is-open' : ''}`}
            onClick={() => setIsOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div className={`nav__mobile ${isOpen ? 'is-open' : ''}`}>
        <nav className="nav__mobile-links">
          {navLinks.map((link) => (
            <a
              key={`m-${link.key}`}
              href={`#${link.key}`}
              className="nav__mobile-link"
              onClick={(e) => {
                e.preventDefault();
                go(link.key);
              }}
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default NavBarComponent;